import { isReasonCode, getReasonCode, ProblemSchema, type Problem } from '@paguz/contracts';
import type { z } from 'zod';
import { apiUrl } from '../config.ts';
import { sessionStore } from '../lib/session.ts';

/** Error de la API con el `Problem` (RFC 9457) ya interpretado. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;

  constructor(status: number, problem: Problem | null, fallback: string) {
    super(problem?.detail ?? problem?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  get reasonCode(): string | null {
    return this.problem?.reasonCode ?? null;
  }

  get traceId(): string | null {
    return this.problem?.traceId ?? null;
  }
}

/** Mensaje para el operador: detalle técnico del catálogo `PZ-*` si existe. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.reasonCode;
    if (error.problem?.detail) return error.problem.detail;
    if (code && isReasonCode(code)) return getReasonCode(code).operatorDetail;
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'Ocurrió un error inesperado.';
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Esquema Zod de la respuesta (de `@paguz/contracts` o de `admin-schemas.ts`). */
  schema?: z.ZodType<T>;
  /** Agrega `Idempotency-Key` (operaciones Idem ✔). */
  idempotent?: boolean;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions<T> = {}): Promise<T> {
  const { method = 'GET', body, schema, idempotent = false, query, signal } = options;
  const url = new URL(apiUrl(path));
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '')
      url.searchParams.set(key, String(value));
  }

  const headers = new Headers({ Accept: 'application/json' });
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (idempotent) headers.set('Idempotency-Key', crypto.randomUUID());
  const session = sessionStore.get();
  if (session) headers.set('Authorization', `Bearer ${session.accessToken}`);

  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  const response = await fetch(url, init);

  if (!response.ok) {
    const problem = await readProblem(response);
    if (response.status === 401 && session) sessionStore.end();
    throw new ApiError(response.status, problem, `Error ${response.status} en ${path}`);
  }
  if (response.status === 204) return undefined as T;

  const json: unknown = await response.json();
  if (!schema) return json as T;
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new Error(`Respuesta inesperada de ${path}: ${parsed.error.issues[0]?.message ?? ''}`);
  }
  return parsed.data;
}

async function readProblem(response: Response): Promise<Problem | null> {
  try {
    const parsed = ProblemSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
